import React, { useState } from 'react';
import InviteTeammates from './InviteTeammates';
import { track } from '../analytics';
import { copyText, inviteLink, sendBulkInvites } from '../utils/invites';
import './CreateWorkspacePanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// `freeform` is the default: a blank workspace with no seeded threads. Every
// other type auto-seeds its suggested threads (welcome + goal-specific) on
// create. Keep `freeform` first so it reads as the natural starting point.
// Consumer-audience entries (`consumer: true`) are hidden from the picker by
// default (strategy.md §5 — mixed brand signals for the eng-org ICP); set the
// REACT_APP_SHOW_CONSUMER_GOALS build flag to re-show them. Workspaces already
// created with these goals are unaffected — only discovery is gated.
const ALL_WORKSPACE_GOALS = [
  {
    id: 'freeform',
    emoji: '💬',
    label: 'Freeform',
    description: 'Start blank — no suggested threads, just an empty workspace.',
  },
  {
    id: 'holiday',
    emoji: '🏖️',
    label: 'Plan a Holiday',
    description: 'Organize trips, share destinations, build itineraries.',
    consumer: true,
  },
  {
    id: 'wedding',
    emoji: '💒',
    label: 'Plan a Wedding',
    description: 'Coordinate venues, vendors, guest lists, and details.',
    consumer: true,
  },
  {
    id: 'event',
    emoji: '🎉',
    label: 'Plan an Event',
    description: 'Organize parties, conferences, meetups, or gatherings.',
  },
  {
    id: 'coffee-review',
    emoji: '☕',
    label: 'Coffee Reviews',
    description: 'Rate and discuss coffee shops, beans, and brewing.',
    consumer: true,
  },
  {
    id: 'project',
    emoji: '📋',
    label: 'Run a Project',
    description: 'Manage milestones, tasks, and team coordination.',
  },
  {
    id: 'build-with-ai',
    emoji: '🛠️',
    label: 'Build with AI',
    description: 'Plan, design, and ship software with an AI teammate.',
  },
  {
    id: 'study',
    emoji: '📚',
    label: 'Study Group',
    description: 'Learn together with shared notes and discussions.',
  },
];

const WORKSPACE_GOALS = ALL_WORKSPACE_GOALS.filter(
  (g) => !g.consumer || process.env.REACT_APP_SHOW_CONSUMER_GOALS === 'true',
);

const SKIP_REASONS = {
  already_member: 'already in the workspace',
  already_invited: 'already has an invite',
  invalid: 'not an email address',
};

/**
 * CreateWorkspacePanel — name the workspace, pick its type, and invite the team
 * in the same step. Everyone added under "Invite your team" is sent through the
 * bulk endpoint, so the whole batch shares **one** invite link, which the
 * result screen shows for copying. A failed invite never undoes the workspace:
 * the result screen explains (e.g. "confirm your email first") and keeps the
 * list so the invites can be retried.
 *
 * `onCreateWorkspace(name, goal, options)` is App's handler — it resolves to
 * `{ workspace }` or `{ error }`.
 */
export default function CreateWorkspacePanel({
  user,
  onCreateWorkspace,
  onClose,
  firstWorkspace = false,
}) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('freeform');
  const [invitees, setInvitees] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  // After the workspace exists: the invite outcome shown on the result screen.
  const [workspace, setWorkspace] = useState(null);
  const [sending, setSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resent, setResent] = useState(false);

  const sendInvites = async (ws) => {
    setSending(true);
    setInviteError(null);
    try {
      const result = await sendBulkInvites(ws.id, invitees);
      setInviteResult(result);
      track('invites-sent', { count: result.invites.length, source: 'create-workspace' });
    } catch (err) {
      setInviteError({ message: err.message, code: err.code });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating || !onCreateWorkspace) return;
    setCreating(true);
    setError('');
    try {
      // `freeform` is the blank default — no goal, no seeded threads. Any other
      // type passes its goal and auto-seeds the suggested threads.
      const isFreeform = !goal || goal === 'freeform';
      const result = await onCreateWorkspace(name.trim(), isFreeform ? null : goal, {
        seedSuggested: !isFreeform,
        inviteeCount: invitees.length,
      });
      // Surface server-side failures (e.g. the workspace-creation rate limit)
      // instead of silently closing the dialog with nothing created.
      if (!result || result.error) {
        setError(result?.error || 'Could not create the workspace');
        return;
      }
      if (invitees.length === 0) {
        onClose();
        return;
      }
      setWorkspace(result.workspace);
      await sendInvites(result.workspace);
    } finally {
      setCreating(false);
    }
  };

  const resendVerification = async () => {
    try {
      await fetch(`${SERVER_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email }),
      });
      setResent(true);
    } catch {
      // The banner in the Lobby offers the same resend; nothing to add here.
    }
  };

  const link = inviteResult?.batchCode ? inviteLink(inviteResult.batchCode) : null;
  const invitedCount = inviteResult?.invites?.length || 0;
  const skipped = inviteResult?.skipped || [];

  return (
    <div className="new-workspace-panel">
      <div className="new-workspace-header">
        <h2>{firstWorkspace ? 'Create your workspace' : 'Create workspace'}</h2>
        <button className="btn-close-panel" onClick={onClose} aria-label="Close">
          x
        </button>
      </div>

      {!workspace ? (
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workspace name, e.g. your team or project…"
            aria-label="Workspace name"
            maxLength={50}
            autoFocus
          />
          <div className="goal-picker">
            <p className="goal-picker-label">What kind of workspace is this?</p>
            <div className="goal-grid">
              {WORKSPACE_GOALS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`goal-card ${goal === g.id ? 'selected' : ''}`}
                  onClick={() => setGoal(g.id)}
                >
                  <span className="goal-emoji">{g.emoji}</span>
                  <span className="goal-label">{g.label}</span>
                  <span className="goal-desc">{g.description}</span>
                </button>
              ))}
            </div>
            <p className="goal-picker-hint">
              {goal && goal !== 'freeform'
                ? 'We’ll set up the suggested threads for this type automatically.'
                : 'Starts empty — add threads whenever you’re ready.'}
            </p>
          </div>

          <section className="new-workspace-invite" aria-label="Invite your team">
            <p className="goal-picker-label">
              Invite your team <span className="new-workspace-optional">optional</span>
            </p>
            <p className="new-workspace-invite-hint">
              Add one person or paste a whole list — everyone gets the same invite link.
            </p>
            {user?.emailVerified === false && (
              <p className="new-workspace-invite-note">
                Invites go out once you’ve confirmed your email — check your inbox for our link.
              </p>
            )}
            <InviteTeammates value={invitees} onChange={setInvitees} disabled={creating} />
          </section>

          {error && (
            <p className="new-workspace-error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={!name.trim() || creating}>
            {creating
              ? 'Creating…'
              : invitees.length > 0
                ? `Create & invite ${invitees.length} ${invitees.length === 1 ? 'person' : 'people'}`
                : 'Create workspace'}
          </button>
        </form>
      ) : (
        <div className="workspace-created" role="status">
          <p className="workspace-created-title">{workspace.name} is ready.</p>

          {sending && <p className="workspace-created-text">Sending invites…</p>}

          {!sending && inviteResult && (
            <>
              {invitedCount > 0 ? (
                <>
                  <p className="workspace-created-text">
                    {inviteResult.emailed
                      ? `We’ve emailed an invite to ${invitedCount} ${invitedCount === 1 ? 'person' : 'people'}.`
                      : `${invitedCount} ${invitedCount === 1 ? 'person is' : 'people are'} invited.`}{' '}
                    Share this link with them too — it’s one link for everyone you invited:
                  </p>
                  <div className="team-link-box">
                    <code className="team-link-url">{link}</code>
                    <button
                      type="button"
                      className="invite-add-btn"
                      onClick={() => {
                        copyText(link);
                        setCopied(true);
                      }}
                    >
                      {copied ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="workspace-created-text">Nobody new to invite.</p>
              )}
              {skipped.length > 0 && (
                <ul className="workspace-created-skipped">
                  {skipped.map((s) => (
                    <li key={s.email}>
                      {s.email} — {SKIP_REASONS[s.reason] || 'skipped'}
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" className="btn-primary" onClick={onClose}>
                Done
              </button>
            </>
          )}

          {!sending && inviteError && (
            <>
              <p className="workspace-created-text">
                {inviteError.code === 'email_unverified'
                  ? `Confirm your email to send your invites — click the link we sent to ${user?.email || 'your inbox'}, then send them from here.`
                  : inviteError.message}
              </p>
              <div className="workspace-created-actions">
                {inviteError.code === 'email_unverified' && (
                  <button
                    type="button"
                    className="invite-link-btn"
                    onClick={resendVerification}
                    disabled={resent}
                  >
                    {resent ? 'Link sent' : 'Resend confirmation link'}
                  </button>
                )}
                <button type="button" className="invite-link-btn" onClick={onClose}>
                  Skip for now
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => sendInvites(workspace)}
                >
                  Send invites
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
